import { useState, useEffect, useCallback } from 'react';
import {
  getNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead
} from '../api/dashboard'; // Import from dashboard API

/**
 * Custom hook for managing general notifications
 * @param {Object} options - Configuration options
 * @param {boolean} options.autoFetch - Auto fetch on mount (default: true)
 * @param {number} options.pollInterval - Poll interval in ms (default: 30000 = 30 seconds)
 * @param {string} options.notificationType - Filter notifications by type (optional)
 */
export const useNotifications = (options = {}) => {
  const { autoFetch = true, pollInterval = 30000, notificationType = null } = options;

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch notifications
   */
  const fetchNotifications = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);

    try {
      const response = await getNotifications({ ...params, notification_type: notificationType });

      // Handle various response formats
      let notifArray = [];
      if (Array.isArray(response)) {
        notifArray = response;
      } else if (response?.success && Array.isArray(response.data)) {
        notifArray = response.data;
      } else if (Array.isArray(response?.data)) {
        notifArray = response.data;
      } else if (Array.isArray(response?.notifications)) {
        notifArray = response.notifications;
      }

      setNotifications(notifArray);
      const currentUnreadCount = notifArray.filter(notif => !notif.is_read).length;
      setUnreadCount(currentUnreadCount);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch notifications');
      // Ensure notifications stays as empty array on error
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  }, [notificationType]);

  /**
   * Mark notification as read
   */
  const markAsRead = useCallback(async (notificationId) => {
    try {
      const response = await markNotificationAsRead(notificationId);

      if (response.success) {
        // Update local state
        setNotifications(prev =>
          prev.map(notif =>
            notif.id === notificationId
              ? { ...notif, is_read: true, read_at: new Date().toISOString() }
              : notif
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
        return { success: true };
      } else {
        throw new Error(response.message || 'Failed to mark notification as read');
      }
    } catch (err) {
      console.error('Error marking notification as read:', err);
      return {
        success: false,
        message: err.response?.data?.message || err.message || 'Failed to mark notification as read'
      };
    }
  }, []);

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = useCallback(async () => {
    try {
      const response = await markAllNotificationsAsRead();

      if (response.success) {
        // Update local state
        setNotifications(prev =>
          prev.map(notif => ({
            ...notif,
            is_read: true,
            read_at: new Date().toISOString()
          }))
        );
        setUnreadCount(0);
        return { success: true };
      } else {
        throw new Error(response.message || 'Failed to mark all notifications as read');
      }
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      return {
        success: false,
        message: err.response?.data?.message || err.message || 'Failed to mark all notifications as read'
      };
    }
  }, []);

  /**
   * Get unread notifications only
   */
  const getUnreadNotifications = useCallback(() => {
    return notifications.filter(notif => !notif.is_read);
  }, [notifications]);

  /**
   * Get read notifications only
   */
  const getReadNotifications = useCallback(() => {
    return notifications.filter(notif => notif.is_read);
  }, [notifications]);

  /**
   * Refetch notifications
   */
  const refetch = useCallback(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Auto fetch on mount if enabled
  useEffect(() => {
    if (autoFetch) {
      fetchNotifications();
    }
  }, [autoFetch, fetchNotifications]);

  // Set up polling if interval is provided
  useEffect(() => {
    if (pollInterval > 0) {
      const intervalId = setInterval(() => {
        fetchNotifications();
      }, pollInterval);

      return () => clearInterval(intervalId);
    }
  }, [pollInterval, fetchNotifications]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    getUnreadNotifications,
    getReadNotifications,
    refetch
  };
};

export default useNotifications;
